import PropTypes from 'prop-types';
import { Edit, MoreVertical, PenTool, Shield, Trash2, UserCheck } from 'lucide-react';
import { Badge, Button } from '../ui';
import { EmptyState } from '../common';

const roleConfig = {
  INACTIF: { label: "Inactif", icon: Shield, className: "bg-destructive/10 text-destructive border-destructive/20" },
  ACTIF: { label: "Actif", icon: PenTool, className: "bg-primary/10 text-primary border-primary/20" },
  PARTICIPANT: { label: "Répondant", icon: UserCheck, className: "bg-accent/10 text-accent border-accent/20" },
}

function UserList({
  users,
  onEdit,
  onSuspend,
  onDelete,
  isLoading = false,
  getRoleLabel,
  getStatusConfig,
}) {
  if (users.length === 0) {
    return (
      <EmptyState
        icon="users"
        title="Aucun gestionnaire trouvé"
        description="Aucun gestionnaire n'a été créé pour le moment."
      />
    );
  }

  return (
    <div className="divide-y divide-border bg-white rounded-lg">
      {users.map((user) => {
        const initials = `${user.first_name?.charAt(0) || ''}${user.last_name?.charAt(0) || ''}`.toUpperCase();
        const role = roleConfig[user.statut];
        const RoleIcon = role?.icon;
        const statusConfig = getStatusConfig(user.statut === 'ACTIF');

        return (
          <div key={user.id} className="flex items-center justify-between p-4 hover:bg-muted/30 transition-colors">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center text-sm font-semibold text-primary">
                {initials}
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">{user.first_name} {user.last_name}</p>
                <p className="text-xs text-muted-foreground">{user.email}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Badge variant="outline" className={role?.className || "bg-gray-100 text-gray-700"}>
                {RoleIcon && <RoleIcon className="h-3 w-3 mr-1" />}{role?.label || user.statut}
              </Badge>
              {user.status === "suspended" && <Badge variant="outline" className="bg-destructive/10 text-destructive text-xs">Suspendu</Badge>}
              <span className="text-xs text-muted-foreground hidden sm:inline">{user.date_joined}</span>
              
              {/* Menu actions */}
              <div className="relative group">
                <div className="flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none data-[state=open]:bg-accent focus:bg-accent">
                  <Button disabled={isLoading} variant="ghost" size="icon"><MoreVertical className="h-4 w-4" /></Button>
                </div>

                {/* Dropdown menu */}
                <div className="absolute right-0 mt-1 z-50 min-w-[8rem] bg-white shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none">
                  {onEdit && (
                    <button
                      onClick={() => onEdit(user)}
                      disabled={isLoading}
                      className="w-full text-left rounded-lg px-4 py-2 text-sm hover:bg-orange-400 hover:text-white flex items-center gap-2 disabled:opacity-50"
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Modification
                    </button>
                  )}
                  {onSuspend && user.status !== 'suspended' && (
                    <button
                      onClick={() => onSuspend(user)}
                      disabled={isLoading}
                      className="w-full text-left px-4 py-2 text-sm text-orange-600 hover:bg-orange-50 flex items-center gap-2 disabled:opacity-50"
                    >
                      Suspendre
                    </button>
                  )}
                  {onDelete && (
                    <button
                      onClick={() => onDelete(user)}
                      disabled={isLoading}
                      className="w-full text-left px-4 py-2 text-sm text-red-600 rounded-lg hover:bg-red-50 flex items-center gap-2 disabled:opacity-50 mt-2 border-t"
                    >
                      <Trash2 className="h-4 w-4 mr-1" /> Supprimer
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

UserList.propTypes = {
  users: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    email: PropTypes.string,
    statut: PropTypes.string,
    status: PropTypes.string,
    date_joined: PropTypes.string,
  })).isRequired,
  onEdit: PropTypes.func,
  onSuspend: PropTypes.func,
  onDelete: PropTypes.func,
  isLoading: PropTypes.bool,
  getRoleLabel: PropTypes.func,
  getStatusConfig: PropTypes.func,
};

UserList.defaultProps = {
  isLoading: false,
  onEdit: null,
  onSuspend: null,
  onDelete: null,
};

export default UserList;
